/**
 * PricingTiersBlock (Feb 2026 — Pricing-Redesign)
 *
 * Zentraler, wiederverwendbarer Block für die 3+1 Bento-Pricing-Architektur.
 *
 * Layout:
 *   ┌──────┬──────┬──────┐
 *   │ Free │ Basic│ Pro  │   ← 3 Plan-Karten (Pro = highlighted)
 *   └──────┴──────┴──────┘
 *   ┌─────────────────────┐
 *   │ Lifetime Banner     │   ← Zinc-900 Premium-Banner (full-width)
 *   └─────────────────────┘
 *
 * Verwendung:
 *   <PricingTiersBlock
 *     plans={plansFromApi}
 *     billingInterval="monthly"
 *     onBillingChange={setBillingInterval}
 *     onSelectPlan={(planId) => ...}
 *     onLifetime={() => ...}
 *     currentPlan={user?.plan}
 *     compact={false}
 *     showLifetime={true}
 *   />
 *
 * Variants:
 *   compact={true}  → keine Toggle, kein Lifetime-Banner; nur 3 Cards (für Modal/Settings).
 *   showLifetime    → Banner ein/aus.
 */
import React from 'react';
import { Check, Sparkles, Zap, Crown, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

// Feature-Gruppen für visuelle Hierarchie (löst „unübersichtlich"-Problem)
const FEATURE_GROUPS = {
  free: [
    {
      title: 'Kunden & Projekte',
      items: ['1 Kunde', '1 Projekt'],
    },
    {
      title: 'Basis-Funktionen',
      items: ['Zeiterfassung mit Timer', 'Ausgaben dokumentieren', 'PDF-Rechnungen erstellen'],
    },
  ],
  basic: [
    {
      title: 'Kunden & Projekte',
      items: ['2 Kunden', '4 Projekte'],
    },
    {
      title: 'Funktionen',
      items: [
        'Alles aus Free',
        'Belegspeicherung (PDF/Foto)',
        'Auto-Rechnungen aus Zeiteinträgen',
        'Angebote mit Online-Annahme',
        'Dashboard & Finanz-Auswertungen',
      ],
    },
  ],
  pro: [
    {
      title: 'Funktionen',
      items: ['Alles aus Basic – inkl. Angebote'],
    },
    {
      title: 'Kunden & Projekte',
      items: ['Unbegrenzt Kunden', 'Unbegrenzt Projekte'],
    },
    {
      title: 'KI & Automatisierung',
      items: [
        'KI-Belegerkennung (Vendor, Datum, Betrag)',
        'Mobile Beleg-Scan mit Auto-Crop',
        'Beleg-Eingang per E-Mail (persönliche Adresse)',
        'Auto-Match Banking ↔ Belege',
      ],
    },
    {
      title: 'Steuern & Export',
      items: [
        'Steuerberechnung (DE/AT/CH)',
        'USt-Voranmeldung (UStVA/UVA)',
        'Anlagevermögen & AfA-Planung (DACH-konform)',
        'DATEV / BMD / ZIP-Bundle für SB',
        'Steuerberater-Anbindung',
      ],
    },
  ],
};

const PLAN_META = {
  free: {
    name: 'Free',
    tagline: 'Zum Ausprobieren',
    icon: Zap,
    iconBg: 'bg-zinc-100 text-zinc-700',
  },
  basic: {
    name: 'Basic',
    tagline: 'Für Solopreneurs',
    icon: Sparkles,
    iconBg: 'bg-blue-100 text-blue-700',
  },
  pro: {
    name: 'Pro',
    tagline: 'Für ernsthafte Freelancer',
    icon: Crown,
    iconBg: 'bg-emerald-100 text-emerald-700',
  },
};

function PlanCard({ tier, plan, isPopular, isCurrent, onSelect, ctaLabel, busy, compact }) {
  const meta = PLAN_META[tier];
  const Icon = meta.icon;
  const groups = FEATURE_GROUPS[tier];

  // Plan-Card-Basis-Klassen (nach Design-Guidelines)
  const baseClasses = 'relative flex flex-col rounded-2xl border bg-white transition-all duration-300';
  const variantClasses = isPopular
    ? 'border-emerald-500 shadow-xl shadow-emerald-500/10 ring-1 ring-emerald-500 md:-translate-y-2'
    : 'border-zinc-200 shadow-sm hover:shadow-md hover:border-zinc-300';
  const padding = compact ? 'p-6' : 'p-8';

  return (
    <div
      className={`${baseClasses} ${variantClasses} ${padding}`}
      data-testid={`plan-card-${tier}`}
      data-popular={isPopular ? 'true' : 'false'}
    >
      {/* "Beliebt" Badge */}
      {isPopular && (
        <span
          className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-emerald-500 px-4 py-1 text-xs font-bold uppercase tracking-widest text-white shadow-md"
          data-testid={`plan-badge-popular-${tier}`}
        >
          Beliebt
        </span>
      )}

      {/* Plan-Header: Icon + Name + Tagline */}
      <div className="flex items-center gap-3 mb-5">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${meta.iconBg}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {meta.name}
          </h3>
          <p className="text-xs text-zinc-500">{meta.tagline}</p>
        </div>
      </div>

      {/* Preis */}
      <div className="mb-6">
        <div className="flex items-baseline gap-1.5">
          <span className="text-5xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            €{plan.price}
          </span>
          {plan.price > 0 && (
            <span className="text-sm text-zinc-500">
              /{plan.interval === 'year' ? 'Jahr' : 'Monat'}
            </span>
          )}
        </div>
        {plan.price === 0 && (
          <p className="text-xs text-zinc-500 mt-1">Für immer kostenlos.</p>
        )}
        {plan.interval === 'year' && plan.price > 0 && (
          <p className="text-xs text-emerald-600 font-medium mt-1">
            Spare 2 Monate (vs. monatlich)
          </p>
        )}
      </div>

      {/* CTA */}
      <button
        type="button"
        onClick={onSelect}
        disabled={busy || isCurrent}
        className={
          isPopular
            ? 'inline-flex items-center justify-center rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-white shadow-sm hover:bg-emerald-600 transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed'
            : 'inline-flex items-center justify-center rounded-full border border-zinc-200 bg-white px-6 py-3 text-sm font-bold text-zinc-900 hover:bg-zinc-50 transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed'
        }
        data-testid={`plan-cta-${tier}`}
      >
        {busy ? '…' : (isCurrent ? '✓ Aktueller Plan' : ctaLabel)}
        {!busy && !isCurrent && <ArrowRight className="h-4 w-4 ml-2" />}
      </button>

      {/* Feature-Gruppen (geclustert für Übersicht) */}
      <div className="mt-7 space-y-5 flex-1">
        {groups.map((group) => (
          <div key={group.title}>
            <h4 className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 mb-2.5">
              {group.title}
            </h4>
            <ul className="space-y-2">
              {group.items.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-zinc-700 leading-snug">
                  <Check className="h-4 w-4 shrink-0 text-emerald-500 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function LifetimeBanner({ price, badge, soldOut, onPurchase, busy }) {
  return (
    <div
      className="relative mt-12 rounded-2xl bg-zinc-900 text-white overflow-hidden shadow-2xl"
      data-testid="lifetime-banner"
    >
      {/* Subtle decorative gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 via-transparent to-transparent" />
      <div className="absolute right-0 top-0 h-full w-1/2 bg-gradient-to-l from-emerald-600/15 to-transparent" />

      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-8 p-8 md:p-10">
        {/* Left: Content */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-9 w-9 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
              <Crown className="h-4.5 w-4.5" strokeWidth={2.2} />
            </div>
            <span className="text-[11px] font-bold uppercase tracking-widest text-emerald-400">
              Early Supporter Deal
            </span>
            {badge && (
              <span className="ml-1 inline-flex items-center rounded-full bg-emerald-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-900">
                {badge}
              </span>
            )}
          </div>

          <h3 className="text-3xl md:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Lifetime — einmal zahlen, nie wieder kümmern
          </h3>
          <p className="text-zinc-300 text-sm md:text-base leading-relaxed max-w-xl">
            Sichere dir alle Pro-Features dauerhaft, inklusive aller zukünftigen Updates.
            Kein Abo, keine wiederkehrenden Kosten — nur einmal zahlen und für immer profitieren.
          </p>

          {/* Mini Feature Pills */}
          <div className="flex flex-wrap gap-2 mt-5">
            {['Alle Pro-Features', 'Lebenslange Updates', 'Kein Abo'].map((f) => (
              <span
                key={f}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-sm px-3 py-1 text-xs font-medium text-zinc-100"
              >
                <Check className="h-3 w-3 text-emerald-400" />
                {f}
              </span>
            ))}
          </div>
        </div>

        {/* Right: Price + CTA */}
        <div className="flex-shrink-0 md:text-right">
          <div className="flex items-baseline gap-2 md:justify-end">
            <span className="text-5xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              €{price || '199'}
            </span>
            <span className="text-sm text-zinc-400">einmalig</span>
          </div>
          <p className="text-xs text-zinc-500 mt-1">zzgl. MwSt. · keine versteckten Kosten</p>

          <button
            type="button"
            onClick={onPurchase}
            disabled={busy || soldOut}
            className="mt-5 inline-flex items-center justify-center rounded-full bg-emerald-500 px-7 py-3.5 text-sm font-bold text-white hover:bg-emerald-400 transition-colors w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="lifetime-cta"
          >
            {busy ? '…' : (soldOut ? 'Ausverkauft' : 'Lifetime sichern')}
            {!busy && !soldOut && <ArrowRight className="h-4 w-4 ml-2" />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PricingTiersBlock({
  plans,
  billingInterval = 'monthly',
  onBillingChange,
  onSelectPlan,
  onLifetime,
  currentPlan = null,
  loadingPlanId = null,
  showLifetime = true,
  showBillingToggle = true,
  lifetimePrice,
  lifetimeBadge,
  lifetimeSoldOut = false,
  compact = false,
  className = '',
}) {
  if (!plans) return null;

  const getPlan = (tier) => {
    if (tier === 'free') return plans.free;
    return billingInterval === 'monthly' ? plans[tier].monthly : plans[tier].yearly;
  };

  const tiers = ['free', 'basic', 'pro'];

  return (
    <div className={`w-full ${className}`} data-testid="pricing-tiers-block">
      {/* Yearly Toggle */}
      {showBillingToggle && onBillingChange && (
        <div className="flex items-center justify-center gap-3 mb-12" data-testid="billing-toggle">
          <span className={`text-sm font-medium transition-colors ${billingInterval === 'monthly' ? 'text-zinc-900' : 'text-zinc-400'}`}>
            Monatlich
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={billingInterval === 'yearly'}
            onClick={() => onBillingChange(billingInterval === 'monthly' ? 'yearly' : 'monthly')}
            className="relative inline-flex h-7 w-13 items-center rounded-full bg-emerald-500 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
            style={{ width: '52px' }}
            data-testid="plan-toggle-yearly"
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                billingInterval === 'yearly' ? 'translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium transition-colors ${billingInterval === 'yearly' ? 'text-zinc-900' : 'text-zinc-400'}`}>
              Jährlich
            </span>
            <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider">
              Spare 17%
            </span>
          </div>
        </div>
      )}

      {/* 3-Card Grid */}
      <div className={`grid grid-cols-1 ${compact ? 'md:grid-cols-3 gap-4' : 'md:grid-cols-3 gap-6 md:gap-7'}`}>
        {tiers.map((tier) => {
          const plan = getPlan(tier);
          if (!plan) return null;
          const isPopular = tier === 'pro';
          const isCurrent = currentPlan === plan.name;
          const ctaLabel = tier === 'free'
            ? 'Kostenlos starten'
            : tier === 'basic' ? 'Basic auswählen' : 'Pro starten';

          return (
            <PlanCard
              key={tier}
              tier={tier}
              plan={plan}
              isPopular={isPopular}
              isCurrent={isCurrent}
              onSelect={() => onSelectPlan && onSelectPlan(plan.name, tier)}
              ctaLabel={ctaLabel}
              busy={loadingPlanId === plan.name}
              compact={compact}
            />
          );
        })}
      </div>

      {/* Lifetime Banner */}
      {showLifetime && onLifetime && (
        <LifetimeBanner
          price={lifetimePrice}
          badge={lifetimeBadge}
          soldOut={lifetimeSoldOut}
          onPurchase={onLifetime}
          busy={loadingPlanId === 'lifetime'}
        />
      )}

      {/* Compare-Link */}
      <div className="text-center mt-8 text-sm text-zinc-500">
        <Link to="/pricing" className="hover:text-emerald-600 hover:underline" data-testid="pricing-compare-link">
          Alle Features im Detail vergleichen →
        </Link>
      </div>
    </div>
  );
}

export { PlanCard, LifetimeBanner };
